import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convexApi";

import {
  MasterTableFooter,
  MasterToolbar,
  useMasterFilter,
} from "./master-filter";

interface GlAccount {
  _id: string;
  qboAccountName: string;
  label: string;
  matchingHints?: string[];
  active: boolean;
}

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function GlAccountsTab({
  authUserId,
  canManage,
}: {
  authUserId: string | undefined;
  canManage: boolean;
}) {
  const accounts = useQuery(
    api.accountingBillMasters.listGlAccounts,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as GlAccount[] | undefined;

  const filter = useMasterFilter<GlAccount>(
    accounts,
    (account) =>
      `${account.qboAccountName} ${account.label} ${(account.matchingHints ?? []).join(" ")}`,
  );

  const counts = React.useMemo(() => {
    const all = accounts ?? [];
    const active = all.filter((account) => account.active).length;
    return { active, inactive: all.length - active, all: all.length };
  }, [accounts]);

  const createGlAccount = useMutation(
    api.accountingBillMasters.createGlAccount,
  );
  const updateGlAccount = useMutation(
    api.accountingBillMasters.updateGlAccount,
  );
  const setGlAccountActive = useMutation(
    api.accountingBillMasters.setGlAccountActive,
  );

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<GlAccount | null>(null);
  const [qboAccountName, setQboAccountName] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [hints, setHints] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const openCreate = () => {
    setEditing(null);
    setQboAccountName("");
    setLabel("");
    setHints("");
    setDialogOpen(true);
  };

  const openEdit = (account: GlAccount) => {
    setEditing(account);
    setQboAccountName(account.qboAccountName);
    setLabel(account.label);
    setHints((account.matchingHints ?? []).join(", "));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!authUserId) return;
    setIsSaving(true);
    try {
      if (editing) {
        await updateGlAccount({
          authUserId,
          glAccountId: editing._id,
          qboAccountName,
          label,
          matchingHints: splitCsv(hints),
        });
        toast.success("GL account updated");
      } else {
        await createGlAccount({
          authUserId,
          qboAccountName,
          label,
          matchingHints: splitCsv(hints),
        });
        toast.success("GL account created");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save GL account",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (account: GlAccount) => {
    if (!authUserId) return;
    try {
      await setGlAccountActive({
        authUserId,
        glAccountId: account._id,
        active: !account.active,
      });
      toast.success(
        account.active ? "GL account deactivated" : "GL account activated",
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not update GL account",
      );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          Allow-listed QBO category accounts for bill allocations. Use the full
          hierarchical name from QuickBooks.
        </p>
        {canManage && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New GL account
          </Button>
        )}
      </div>

      <MasterToolbar
        search={filter.search}
        onSearchChange={filter.setSearch}
        status={filter.status}
        onStatusChange={filter.setStatus}
        counts={counts}
        placeholder="Search account name or label"
      />

      {accounts === undefined || filter.visible === undefined ? (
        <Skeleton className="h-32 w-full" />
      ) : accounts.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          No GL accounts yet. Use the Import tab to bootstrap the catalog from
          QuickBooks.
        </p>
      ) : filter.visible.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          No GL accounts match this filter.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>QBO account</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Matching hints</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="w-32" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filter.visible.map((account) => (
                <TableRow key={account._id}>
                  <TableCell className="py-1.5 font-medium">
                    {account.qboAccountName}
                  </TableCell>
                  <TableCell className="py-1.5">{account.label}</TableCell>
                  <TableCell className="text-muted-foreground py-1.5">
                    {(account.matchingHints ?? []).join(", ") || "—"}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Badge variant={account.active ? "secondary" : "outline"}>
                      {account.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="py-1.5">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Edit GL account"
                          onClick={() => openEdit(account)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(account)}
                        >
                          {account.active ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {filter.filtered !== undefined && (
        <MasterTableFooter
          shown={filter.visible?.length ?? 0}
          total={filter.filtered.length}
          hasMore={filter.hasMore}
          onShowMore={filter.showMore}
          onShowAll={filter.showAll}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit GL account" : "New GL account"}
            </DialogTitle>
            <DialogDescription>
              Example: "Expenses:Freight &amp; Delivery:Inbound Freight".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="gl-qbo-name">QBO account name</Label>
              <Input
                id="gl-qbo-name"
                value={qboAccountName}
                onChange={(e) => setQboAccountName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gl-label">Label</Label>
              <Input
                id="gl-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Short label for pickers"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gl-hints">Matching hints (comma-separated)</Label>
              <Input
                id="gl-hints"
                value={hints}
                onChange={(e) => setHints(e.target.value)}
                placeholder="freight, trucking, drayage"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !qboAccountName.trim() || !label.trim()}
            >
              {isSaving ? "Saving…" : editing ? "Save account" : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
